/**
 * DebtPlanHero — Phase 86.E Pass B.
 *
 * The dark-green hero for /accounts?tab=liabilities, rendered strictly from
 * `GET /api/debt-plan`. It shows a debt-free date ONLY when the backend says
 * every debt is projectable (`payoff_path.headline_eligible`); otherwise it
 * reports coverage and what is missing. The old card printed a date regardless,
 * which is the bug this replaces.
 */
import React, { useState } from 'react';
import { CalendarCheck, AlertTriangle, ChevronDown, ChevronUp, Clock, Target } from 'lucide-react';
import { formatMoney } from '@/lib/format';
import {
  longMonth, horizon, dueLabel, SOURCE_LABEL, CONFIDENCE_LABEL,
} from '@/lib/debtPlanFormat';
import DebtScopeDisclosure from './DebtScopeDisclosure';

function PathPanel({ plan }) {
  const { payoff_path: path, scope } = plan;
  const missing = (scope.excluded || []).filter(
    (e) => e.reason === 'no_payment_recorded' || e.reason === 'payment_below_interest');

  if (missing.length > 0) {
    return (
      <div className="briefing-hero__panel p-4 min-w-0" data-testid="debt-plan-missing-panel">
        <p className="text-[10px] uppercase tracking-widest briefing-hero__accent font-medium mb-2">
          What&apos;s missing
        </p>
        <ul className="space-y-2">
          {missing.map((m) => (
            <li key={m.id} className="text-xs">
              <span className="font-medium">{m.name}</span>
              <span className="briefing-hero__muted"> — {m.reason_label}</span>
            </li>
          ))}
        </ul>
        <p className="text-[11px] briefing-hero__muted mt-3">
          Add these and this becomes one date you can trust.
        </p>
      </div>
    );
  }

  return (
    <div className="briefing-hero__panel p-4 min-w-0" data-testid="debt-plan-path-panel">
      <p className="text-[10px] uppercase tracking-widest briefing-hero__accent font-medium mb-2">
        The order they clear
      </p>
      <ul className="space-y-2">
        {(path.order || []).map((o, i) => (
          <li key={o.debt_id} className="flex items-baseline justify-between gap-3 text-xs"
              data-testid={`debt-plan-order-${o.debt_id}`}>
            <span className="min-w-0 truncate">
              <span className="briefing-hero__accent font-medium">{i + 1}.</span>{' '}
              {o.name}
            </span>
            <span className={`shrink-0 tabular-nums ${i === (path.order.length - 1) ? 'font-medium' : 'briefing-hero__muted'}`}>
              {longMonth(o.payoff_date)}
            </span>
          </li>
        ))}
      </ul>
      {path.interest_by_currency && Object.keys(path.interest_by_currency).length > 0 && (
        <p className="text-[11px] briefing-hero__muted mt-3">
          Interest still to pay:{' '}
          {Object.entries(path.interest_by_currency).map(([c, v], i) => (
            <span key={c}>{i > 0 ? ' + ' : ''}{formatMoney(v, c, { compact: true })}</span>
          ))}
        </p>
      )}
    </div>
  );
}

export function DebtPlanHero({ plan }) {
  const [open, setOpen] = useState(false);
  const { payoff_path: path, scope, urgency, goal_link: goalLink } = plan;
  const eligible = path.headline_eligible;
  const next = urgency?.next_payment;

  return (
    <div className="briefing-hero p-5 sm:p-6 mb-4" data-testid="debt-plan-hero">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-[10px] uppercase tracking-widest briefing-hero__accent font-medium">
          Your debt-free plan
        </p>
        <span className="briefing-hero__pill text-[11px] px-2.5 py-1 rounded-full"
              data-testid="debt-plan-confidence">
          {CONFIDENCE_LABEL[path.confidence] || path.confidence}
        </span>
      </div>

      <div className="mt-3 grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(260px,38%)]">
        <div className="min-w-0">
          {eligible ? (
            <>
              <p className="font-display font-medium leading-tight"
                 style={{ fontSize: 'clamp(24px, 2.4vw, 32px)' }}
                 data-testid="debt-plan-headline">
                <CalendarCheck className="inline h-6 w-6 mb-1.5 mr-2 briefing-hero__accent" aria-hidden="true" />
                {scope.group_label
                  ? `Your ${scope.group_label} clear by ${longMonth(path.payoff_date)}`
                  : `Debt-free by ${longMonth(path.payoff_date)}`}
              </p>
              <p className="text-sm briefing-hero__muted mt-1.5" data-testid="debt-plan-subhead">
                {horizon(path.months)} away at your current payments · covers {path.coverage_label}
              </p>
            </>
          ) : (
            <>
              <p className="font-display font-medium leading-tight"
                 style={{ fontSize: 'clamp(22px, 2.2vw, 30px)' }}
                 data-testid="debt-plan-headline">
                <AlertTriangle className="inline h-5 w-5 mb-1.5 mr-2 briefing-hero__accent" aria-hidden="true" />
                {path.projectable
                  ? `This plan covers ${path.coverage_label}`
                  : 'Not enough detail to plan your payoff yet'}
              </p>
              <p className="text-sm briefing-hero__muted mt-1.5" data-testid="debt-plan-subhead">
                {path.projectable
                  ? `No debt-free date yet — the debts on the right aren't in the maths. What is counted clears in ${horizon(path.months)}.`
                  : 'Add a monthly payment (and a rate, if you have it) to each debt and this becomes a real plan.'}
              </p>
            </>
          )}

          {plan.interest_cost?.headline && (
            <p className="text-xs briefing-hero__muted mt-2" data-testid="debt-plan-interest-line">
              {plan.interest_cost.headline.label}:{' '}
              <strong className="font-medium">
                {formatMoney(plan.interest_cost.headline.amount,
                             plan.interest_cost.headline.currency, { compact: true })}
              </strong>
              {' '}
              {plan.interest_cost.headline.qualifier === 'recorded' ? 'recorded' : 'estimated'}
              {plan.interest_cost.headline.partial ? ', plus some months estimated' : ''}
            </p>
          )}

          {scope.debts_outside_group > 0 && (
            <p className="text-xs briefing-hero__muted mt-2" data-testid="debt-plan-scope-note">
              This view covers your {scope.group_label} only —{' '}
              {scope.debts_outside_group} other debt
              {scope.debts_outside_group === 1 ? '' : 's'} {scope.debts_outside_group === 1 ? 'is' : 'are'}{' '}
              not in these numbers. Switch to All for the whole picture.
            </p>
          )}

          {next && (
            <div className="briefing-hero__panel mt-4 px-3.5 py-2.5 inline-flex flex-wrap items-center gap-2"
                 data-testid="debt-plan-urgency">
              <Clock className={`h-4 w-4 ${next.overdue ? 'text-red-300' : 'briefing-hero__accent'}`} aria-hidden="true" />
              <span className="text-xs">
                <strong className="font-medium">{next.name}</strong>{' '}
                {dueLabel(next)}
                {next.amount ? ` · ${formatMoney(next.amount, next.currency)}` : ''}
              </span>
              {next.source && (
                <span className="text-[10px] briefing-hero__muted">
                  ({SOURCE_LABEL[next.source] || next.source})
                </span>
              )}
            </div>
          )}

          {goalLink?.unlinked_goals?.length > 0 && (
            <p className="text-[11px] briefing-hero__muted mt-3 flex items-start gap-1.5"
               data-testid="debt-plan-goal-unlinked">
              <Target className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                Your goal “{goalLink.unlinked_goals[0]}” isn&apos;t linked to any debt yet, so its
                progress isn&apos;t coming from what you actually owe.
              </span>
            </p>
          )}

          {goalLink?.mismatch && (
            <p className="text-[11px] briefing-hero__muted mt-3 flex items-start gap-1.5"
               data-testid="debt-plan-goal-mismatch">
              <Target className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                Your goal “{goalLink.goals?.find((g) => g.has_links)?.title}” doesn&apos;t include{' '}
                {goalLink.unlinked_debts.map((d) => d.name).join(', ')} — reaching it isn&apos;t
                the same as being debt-free.
              </span>
            </p>
          )}
        </div>

        <PathPanel plan={plan} />
      </div>

      <div className="mt-4 pt-3.5 border-t border-white/10">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="briefing-hero__secondary text-xs rounded-full px-3 py-1.5 inline-flex items-center gap-1.5"
          data-testid="debt-plan-disclosure-toggle"
        >
          {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          {open ? 'Hide what\u2019s included' : 'See what\u2019s included'}
        </button>
        {open && <DebtScopeDisclosure scope={scope} />}
      </div>
    </div>
  );
}

export default DebtPlanHero;
